import React from "react";
import ReactDOM from "react-dom/client";
import { client } from "@thyra/client/client.gen";
import { getApiBaseUrl } from "./env";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter } from "react-router-dom";
import {
  createLandingI18n,
  DEFAULT_LOCALE,
  I18nextProvider,
  isLocalizedRoute,
  loadLocaleResources,
  splitLocalePath,
} from "@thyra/landing-i18n";
import { seedPreload } from "./lib/blog-queries";
import App from "./App";
import "aos/dist/aos.css";
import "./index.css";

const queryClient = new QueryClient();
// Blog data the page was prerendered from (see scripts/prerender.mjs); seeded
// before the first render so hydration matches, then refetched as stale.
seedPreload(queryClient, window.__BLOG_PRELOAD__ ?? []);

client.setConfig({
  baseURL: getApiBaseUrl(),
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

/**
 * Prerendered pages ship static SEO tags between ssr-seo-start/end comment
 * markers (see scripts/prerender.mjs). Helmet re-creates them on mount, so
 * drop the static set first to avoid duplicate title/meta/canonical tags.
 */
function removePrerenderedSeoTags() {
  const nodes = Array.from(document.head.childNodes);
  const isMarker = (node: ChildNode, text: string) =>
    node.nodeType === Node.COMMENT_NODE && node.textContent?.trim() === text;
  const start = nodes.findIndex((n) => isMarker(n, "ssr-seo-start"));
  const end = nodes.findIndex((n) => isMarker(n, "ssr-seo-end"));
  if (start === -1 || end === -1 || end < start) return;
  for (const node of nodes.slice(start, end + 1)) node.remove();
}

/**
 * The URL decides the language (see the redirect script in index.html for
 * first-visit detection). Its strings load before the first render so
 * hydration matches the prerendered HTML.
 */
async function bootstrap() {
  const { locale, path } = splitLocalePath(window.location.pathname);
  const i18n = createLandingI18n(locale, await loadLocaleResources(locale));
  document.documentElement.lang = isLocalizedRoute(path)
    ? locale
    : DEFAULT_LOCALE;

  const container = document.getElementById("root")!;
  const app = (
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemeProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </I18nextProvider>
    </React.StrictMode>
  );

  // Prerendered routes ship real HTML (see scripts/prerender.mjs), so hydrate
  // it. Non-prerendered routes (redirects, SPA fallback) still client-render.
  // A /blog/* URL with no prerendered file (post or tag published after the
  // last build) is answered by server.mjs with dist/blog/__shell.html: a
  // generic placeholder, not this route's markup, so it is replaced rather
  // than hydrated. The route then fetches the post and sets the real
  // title/canonical/robots through <SEO>, dropping the shell's noindex.
  const isBlogShell = container.querySelector("[data-blog-shell]") !== null;
  if (container.firstElementChild && !isBlogShell) {
    removePrerenderedSeoTags();
    ReactDOM.hydrateRoot(container, app);
  } else {
    if (isBlogShell) {
      removePrerenderedSeoTags();
      container.replaceChildren();
    }
    ReactDOM.createRoot(container).render(app);
  }
}

void bootstrap();
